"use client";

/**
 * Landing Page - Footer Component
 */

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Send, Check } from "lucide-react";
import { cn } from "@/shared/core/utils";
import { Input } from "@/shared/presentation/components/ui/input";
import { Button } from "@/shared/presentation/components/ui/button";
import { IframeModal, Spinner } from "@/shared/presentation/components/custom";
import { siteConfig } from "@/config/site";
import {
  useSubscribeToMailingList,
  landingValidation,
} from "../../domain/hooks";
import { FOOTER_SECTIONS } from "../../constants";

interface FooterProps {
  checkCaptcha: () => Promise<string | null>;
}

export function Footer({ checkCaptcha }: FooterProps) {
  const t = useTranslations();
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const {
    mutateAsync: subscribe,
    isPending: isLoading,
    isSuccess: isEmailSent,
  } = useSubscribeToMailingList();

  const updateEmail = (input: string) => {
    setIsEmailValid(landingValidation.isEmailValid(input));
    setEmail(input);
  };

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const recaptchaToken = await checkCaptcha();

    if (recaptchaToken === null) {
      return;
    }

    await subscribe({
      email,
      captchaToken: recaptchaToken,
    });

    setEmail("");
  };

  const handleInfoClick = (action: string) => {
    if (action === "privacy") {
      setShowPrivacy(true);
    } else if (action === "terms") {
      setShowTerms(true);
    }
  };

  return (
    <>
      <footer className="bg-primary-dark px-8 py-20 text-left">
        <div className="mx-auto grid max-w-5xl grid-cols-2 justify-between gap-6 md:grid-cols-4">
          {/* Agarea Links */}
          <div className="flex flex-1 flex-col gap-2">
            <span className="mb-4 text-lg font-semibold uppercase text-white">
              {siteConfig.name}
            </span>
            {FOOTER_SECTIONS.agarea.map((link, index) => (
              <a
                key={index}
                className="text-white/75 transition-all hover:text-white"
                href={link.href}
              >
                {t(link.labelKey)}
              </a>
            ))}
          </div>

          {/* Information Links */}
          <div className="flex flex-1 flex-col gap-2">
            <span className="mb-4 text-lg font-semibold text-white">
              {t("landing.footer.information")}
            </span>
            {FOOTER_SECTIONS.information.map((link, index) => (
              <Button
                key={index}
                variant="link"
                className="h-auto justify-start p-0 text-white/75 hover:text-white hover:no-underline"
                onClick={() =>
                  "action" in link
                    ? handleInfoClick(link.action)
                    : window.location.assign(link.href)
                }
              >
                {t(link.labelKey)}
              </Button>
            ))}
          </div>

          {/* Support Links */}
          <div className="flex flex-1 flex-col gap-2">
            <span className="mb-4 text-lg font-semibold text-white">
              {t("landing.footer.support")}
            </span>
            {FOOTER_SECTIONS.support.map((link, index) => (
              <a
                key={index}
                className="text-white/75 transition-all hover:text-white"
                href={link.href}
              >
                {t(link.labelKey)}
              </a>
            ))}
          </div>

          {/* Subscribe Form */}
          <div className="flex flex-1 flex-col gap-2">
            <span className="mb-4 text-lg font-semibold text-white">
              {t("landing.footer.subscribe")}
            </span>
            <form
              onSubmit={sendEmail}
              className="flex w-full justify-between gap-2 rounded-lg bg-background p-2"
            >
              <Input
                className="w-full flex-1 rounded-[30px] border border-solid border-background bg-background p-2 text-left text-base text-primary-dark outline-none"
                type="email"
                placeholder={t("landing.footer.emailPlaceholder")}
                onChange={(e) => updateEmail(e.target.value)}
                value={email}
              />

              {isEmailSent ? (
                <div className="rounded-full px-4 py-2 text-center text-primary transition-all">
                  <Check className="h-5 w-5" />
                </div>
              ) : (
                <Button
                  type="submit"
                  variant="ghost"
                  size="icon"
                  disabled={!isEmailValid || isLoading}
                  className={cn(
                    "rounded-full px-4 py-2 text-center text-primary-dark transition-all",
                    {
                      "cursor-not-allowed": !isEmailValid,
                      "cursor-pointer hover:text-primary": isEmailValid,
                    }
                  )}
                >
                  {isLoading ? (
                    <Spinner size="sm" useIcon />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </Button>
              )}
            </form>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <IframeModal
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        src="/privacy_policy.html"
        title={t("legal.privacyPolicy")}
      />

      {/* Terms of Use Modal */}
      <IframeModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        src="/terms_of_use.html"
        title={t("legal.termsOfService")}
      />
    </>
  );
}

export default Footer;
